import { Component, ComponentInterface, Element, Event, EventEmitter, h, Host, Method, Prop, Watch } from "@stencil/core";
import { clamp, isValidNumber, toMaxPrecision } from "../../utils/number-utils/numbers";
import { Direction, FormatFn, ParseFn } from "../input-field-number/interface";
import { isKeyboardClickKey } from "./utils";
import { ButtonSize } from "../button/button.enum";

@Component({
    tag: "ux-stepper",
    styleUrl: "stepper.scss"
})
export class Stepper implements ComponentInterface {
    private inputFieldNumberElement: HTMLUxInputFieldNumberElement;
    private isStepUpDisabled = false;
    private isStepDownDisabled = false;
    private timerId: ReturnType<typeof setTimeout>;

    @Element() domNode!: HTMLUxStepperElement;

    /**
     * The name attribute of the input element
     */
    @Prop() readonly name: string = "";

    /**
     * The placeholder text.
     */
    @Prop() readonly placeholder: string;

    /**
     * The label.
     */
    @Prop() readonly label?: string;

    /**
     * The default value of the input after initialization.
     */
    @Prop() readonly defaultValue?: number;

    /**
     * A valid number or `Number.NaN`.
     */
    @Prop({ mutable: true }) value: number;

    /**
     * Set to `true` to disable the input.
     */
    @Prop() readonly disabled: boolean = false;

    /**
     * Set to `true` to mark the input as required.
     */
    @Prop() readonly required: boolean = false;

    /**
     * Information with low emphasis.
     */
    @Prop() readonly hintText?: string;

    /**
     * If set to `true`, the state is considered invalid.
     */
    @Prop({ mutable: true }) error = false;

    /**
     * Information to show if the value is invalid.
     */
    @Prop() readonly errorMsg?: string;

    /**
     * The locale to use for input validation.
     */
    @Prop() readonly locale?: string;

    /**
     * The minimum value.
     */
    @Prop() readonly min?: number;

    /**
     * The maximum value.
     */
    @Prop() readonly max?: number;

    /**
     * The increment size to use when using keyboard navigation.
     */
    @Prop() readonly step: number = 1;

    /**
     * The maximum number of decimal places.
     */
    @Prop() readonly precision: number = 0;

    /**
     * The unit of the value, e.g "mm", "kg".
     */
    @Prop() readonly unit?: string;

    /**
     * A function that returns a string representation of a given number.
     */
    @Prop() readonly formatter: FormatFn;

    /**
     * A function that parses a number from a given string.
     */
    @Prop() readonly parser: ParseFn;

    /**
     * Emitted when the value has changed.
     */
    @Event() uxChange!: EventEmitter;

    @Watch("disabled")
    handleDisabledChanged() {
        this.checkDisabled();
        this.clearStepTimer();
    }

    /**
     * Performs a validity check on the input and sets `invalid` accordingly.
     */
    @Method()
    async validate() {
        this.inputFieldNumberElement.validate();
        this.error = this.inputFieldNumberElement.error;
    }

    componentWillRender() {
        if (this.value === undefined) {
            // @ts-expect-error Check for typing
            this.value = this.defaultValue;
        }
        this.checkDisabled();
    }

    disconnectedCallback() {
        this.clearStepTimer();
    }

    private checkDisabled() {
        this.isStepUpDisabled = this.disabled || (this.max == null ? false : this.value >= this.max);
        this.isStepDownDisabled = this.disabled || (this.min == null ? false : this.value <= this.min);
    }

    private setStepTimer(direction: Direction) {
        const loopStep = () => {
            this.checkDisabled();
            if (this.isStepDownDisabled || this.isStepUpDisabled) {
                return;
            }
            this.handleButtonClick(direction);
            this.timerId = setTimeout(loopStep, 100);
        };
        this.timerId = setTimeout(loopStep, 600);
    }

    private clearStepTimer() {
        clearTimeout(this.timerId);
    }

    private getStepDelta(direction: Direction) {
        return direction * this.step;
    }

    private incrementValue(delta: number) {
        const currentValue = isValidNumber(this.value) ? this.value : isValidNumber(this.defaultValue) ? this.defaultValue : 0;
        // @ts-expect-error Check for typing
        let nextValue = toMaxPrecision(currentValue + delta, 0);
        nextValue = clamp(nextValue, this.min, this.max);

        this.value = nextValue;
        this.uxChange.emit();
    }

    private handleButtonClick(direction: Direction) {
        const delta = this.getStepDelta(direction);
        this.incrementValue(delta);
    }

    private getButtonEventHandlers(direction: Direction) {
        return {
            onMouseUp: () => this.clearStepTimer(),
            onMouseLeave: () => this.clearStepTimer(),
            onMouseDown: () => {
                this.handleButtonClick(direction);
                this.setStepTimer(direction);
            },
            onKeyDown: (e: KeyboardEvent) => {
                if (isKeyboardClickKey(e.code)) {
                    this.handleButtonClick(direction);
                }
            }
        };
    }

    private handleInputChange = (e: Event) => {
        this.value = (e.target as HTMLUxInputFieldNumberElement).value;
    };

    render() {
        return (
            <Host class="ux-stepper">
                <ux-input-field-number
                    class="ux-stepper__input-field"
                    // @ts-expect-error Check for typing
                    ref={(el) => (this.inputFieldNumberElement = el)}
                    label={this.label}
                    value={this.value}
                    defaultValue={this.defaultValue}
                    placeholder={this.placeholder}
                    disabled={this.disabled}
                    required={this.required}
                    error={this.error}
                    errorMsg={this.errorMsg}
                    hintText={this.hintText}
                    locale={this.locale}
                    min={this.min}
                    max={this.max}
                    precision={this.precision}
                    step={this.step}
                    unit={this.unit}
                    formatter={this.formatter}
                    parser={this.parser}
                    onUxChange={this.handleInputChange}
                >
                    <ux-button
                        slot="buttons"
                        tabindex="-1"
                        class="ux-stepper__decrease"
                        icon="minus"
                        size={ButtonSize.Icon}
                        disabled={this.isStepDownDisabled}
                        {...this.getButtonEventHandlers(Direction.DOWN)}
                    />
                    <ux-button
                        slot="buttons"
                        tabindex="-1"
                        class="ux-stepper__increase"
                        icon="plus"
                        size={ButtonSize.Icon}
                        disabled={this.isStepUpDisabled}
                        {...this.getButtonEventHandlers(Direction.UP)}
                    />
                </ux-input-field-number>
            </Host>
        );
    }
}
